/**
 * NFC Card Designer V2 - Redesigned with Dieter Rams principles
 *
 * Guided 3-step flow:
 * 1. Choose Your Design
 * 2. Preview & Customize
 * 3. Add to Cart
 */

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  STOCK_DESIGNS,
  PRESET_PROMPTS,
  STYLE_VARIATIONS,
  generateStylePrompt
} from '../lib/stock-designs';
import StepIndicator from './StepIndicator';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import useSimpleCartStore from '../store/simpleCartStore';
import { theme } from '../theme';

export default function CardDesignerV2({ onDesignComplete, showError, showSuccess, showInfo }) {
  // State
  const [step, setStep] = useState(1); // 1: Choose Design, 2: Customize, 3: Review
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [designCategory, setDesignCategory] = useState('gradients');
  const categories = Object.keys(STOCK_DESIGNS);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Cart store
  const { addToCart, isInCart } = useSimpleCartStore();

  // Inject spinner animation
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onEscape: () => {
      if (step > 1) {
        setStep(step - 1);
      }
    },
    onEnter: () => {
      if (step < 3 && selectedDesign) {
        setStep(step + 1);
      }
    },
  });

  // Product specs
  const productSpecs = {
    name: 'cloudcard NFC Card',
    material: 'Premium PVC',
    dimensions: '85.6 × 53.98mm (Credit card size)',
    thickness: '0.76mm',
    technology: 'NFC Type 2 (NTAG216)',
    memory: '888 bytes',
    readRange: 'Up to 10cm',
    price: 2999 // $29.99
  };

  // Render canvas on mount
  useEffect(() => {
    renderPreview(null);
  }, []);

  // Handle design selection
  const handleSelectDesign = (design) => {
    setSelectedDesign(design);
    renderPreview(design);
    setStep(2); // Auto-advance to customization step
  };

  // Handle AI generation (from prompt or style variation)
  const handleGenerateAI = async (promptOverride = null) => {
    const finalPrompt = promptOverride || customPrompt;

    if (!finalPrompt) {
      showError?.('Please enter a design prompt');
      return;
    }

    setIsGenerating(true);
    setGenerationStatus('Initializing AI model...');

    try {
      setGenerationStatus('Sending request to Replicate...');

      const response = await fetch('/api/generate-design', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: finalPrompt,
          model: 'flux-schnell',
          aspectRatio: '1:1',
          numOutputs: 1
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      setGenerationStatus('Generating your design...');
      const data = await response.json();

      if (data.success) {
        setGenerationStatus('Rendering preview...');
        const aiDesign = {
          id: `ai-${Date.now()}`,
          name: promptOverride ? 'AI Styled' : 'AI Generated',
          category: 'ai-generated',
          imageUrl: data.imageUrl,
          prompt: finalPrompt
        };
        setSelectedDesign(aiDesign);
        renderPreview(aiDesign);
        setStep(2);
        setGenerationStatus('');
      } else {
        showError?.(`Generation failed: ${data.error || 'Unknown error'}`);
        setGenerationStatus('');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      if (error.message.includes('404')) {
        showInfo?.('AI generation requires production deployment or wrangler pages dev. Use stock designs for now!');
      } else {
        showError?.(`Generation failed: ${error.message}`);
      }
      setGenerationStatus('');
    } finally {
      setIsGenerating(false);
    }
  };

  // Apply style variation to current design
  const handleApplyStyle = async (styleVariation) => {
    if (!selectedDesign) {
      showError?.('Please select a design first');
      return;
    }

    const styledPrompt = generateStylePrompt(selectedDesign, styleVariation);
    await handleGenerateAI(styledPrompt);
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError?.('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError?.('File too large. Max 5MB');
      return;
    }

    setIsGenerating(true);

    // Use FileReader for local preview in dev mode
    const reader = new FileReader();
    reader.onload = (e) => {
      const uploadDesign = {
        id: `upload-${Date.now()}`,
        name: file.name,
        category: 'uploaded',
        imageUrl: e.target.result
      };
      setSelectedDesign(uploadDesign);
      renderPreview(uploadDesign);
      setStep(2);
      setIsGenerating(false);
    };
    reader.readAsDataURL(file);
  };

  // Render canvas preview
  const renderPreview = (design) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    // No design? Show branded base card
    if (!design) {
      drawBaseCard(ctx, width, height);
      return;
    }

    // Draw clean background
    ctx.fillStyle = theme.surface;
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = theme.border;
    ctx.lineWidth = 2;
    ctx.strokeRect(10, 10, width - 20, height - 20);

    // Apply design
    if (design.category === 'gradient') {
      applyGradient(ctx, design, width, height);
    } else if (design.category === 'pattern') {
      applyPattern(ctx, design, width, height);
    } else if (design.imageUrl) {
      applyImage(ctx, design.imageUrl, width, height);
    }
  };

  const drawBaseCard = (ctx, width, height) => {
    ctx.fillStyle = theme.surface;
    ctx.fillRect(0, 0, width, height);

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, 'rgba(14, 165, 233, 0.15)');
    gradient.addColorStop(1, 'rgba(59, 130, 246, 0.15)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    ctx.font = 'bold 42px Syne, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const textGradient = ctx.createLinearGradient(width / 2 - 120, 0, width / 2 + 120, 0);
    textGradient.addColorStop(0, theme.accent);
    textGradient.addColorStop(1, theme.accentAlt);
    ctx.fillStyle = textGradient;
    ctx.fillText('cloudcard', width / 2, height / 2 - 10);

    ctx.font = '12px DM Sans, sans-serif';
    ctx.fillStyle = theme.textMuted;
    ctx.fillText('☁ Smart NFC Business Cards', width / 2, height / 2 + 30);

    ctx.strokeStyle = theme.border;
    ctx.lineWidth = 2;
    ctx.strokeRect(10, 10, width - 20, height - 20);

    ctx.font = '14px DM Sans, sans-serif';
    ctx.fillStyle = theme.textMuted;
    ctx.fillText('Select a design to begin', width / 2, height / 2 + 60);
  };

  const applyGradient = (ctx, design, width, height) => {
    let gradient;
    if (design.type === 'linear') {
      const angle = (design.angle || 0) * Math.PI / 180;
      const x1 = width / 2 - Math.cos(angle) * width / 2;
      const y1 = height / 2 - Math.sin(angle) * height / 2;
      const x2 = width / 2 + Math.cos(angle) * width / 2;
      const y2 = height / 2 + Math.sin(angle) * height / 2;
      gradient = ctx.createLinearGradient(x1, y1, x2, y2);
    } else {
      gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, width / 2);
    }
    design.colors.forEach((color, index) => {
      gradient.addColorStop(index / (design.colors.length - 1), color);
    });
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    ctx.globalAlpha = 1.0;
  };

  const applyPattern = (ctx, design, width, height) => {
    // Background
    ctx.fillStyle = design.background;
    ctx.fillRect(0, 0, width, height);

    // Apply pattern based on type
    ctx.strokeStyle = design.foreground;
    ctx.lineWidth = 2;

    switch (design.patternType) {
      case 'grid':
        const gridSize = 40;
        for (let x = 0; x < width; x += gridSize) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, height);
          ctx.stroke();
        }
        for (let y = 0; y < height; y += gridSize) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(width, y);
          ctx.stroke();
        }
        break;

      case 'dots':
        const dotSpacing = 30;
        const dotSize = 3;
        ctx.fillStyle = design.foreground;
        for (let x = dotSpacing; x < width; x += dotSpacing) {
          for (let y = dotSpacing; y < height; y += dotSpacing) {
            ctx.beginPath();
            ctx.arc(x, y, dotSize, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        break;

      case 'waves':
        ctx.lineWidth = 3;
        for (let offset = 0; offset < height; offset += 40) {
          ctx.beginPath();
          for (let x = 0; x < width; x++) {
            const y = offset + Math.sin(x * 0.02) * 20;
            if (x === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
        break;

      case 'circuit':
        ctx.lineWidth = 1;
        // Horizontal and vertical lines
        for (let i = 0; i < 20; i++) {
          const x = Math.random() * width;
          const y = Math.random() * height;
          const len = 50 + Math.random() * 100;
          ctx.beginPath();
          if (Math.random() > 0.5) {
            ctx.moveTo(x, y);
            ctx.lineTo(x + len, y);
          } else {
            ctx.moveTo(x, y);
            ctx.lineTo(x, y + len);
          }
          ctx.stroke();
        }
        // Connection nodes
        ctx.fillStyle = design.foreground;
        for (let i = 0; i < 15; i++) {
          const x = Math.random() * width;
          const y = Math.random() * height;
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, Math.PI * 2);
          ctx.fill();
        }
        break;

      case 'hexagons':
        const hexSize = 30;
        for (let y = 0; y < height + hexSize * 2; y += hexSize * 1.5) {
          for (let x = 0; x < width + hexSize * 2; x += hexSize * Math.sqrt(3)) {
            const offsetX = (y / (hexSize * 1.5)) % 2 === 0 ? 0 : hexSize * Math.sqrt(3) / 2;
            drawHexagon(ctx, x + offsetX, y, hexSize);
          }
        }
        break;

      case 'diagonal':
        ctx.lineWidth = 4;
        for (let offset = -height; offset < width + height; offset += 30) {
          ctx.beginPath();
          ctx.moveTo(offset, 0);
          ctx.lineTo(offset + height, height);
          ctx.stroke();
        }
        break;
    }
  };

  const drawHexagon = (ctx, x, y, size) => {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const hx = x + size * Math.cos(angle);
      const hy = y + size * Math.sin(angle);
      if (i === 0) ctx.moveTo(hx, hy);
      else ctx.lineTo(hx, hy);
    }
    ctx.closePath();
    ctx.stroke();
  };

  const applyImage = (ctx, imageUrl, width, height) => {
    const img = new Image();
    if (imageUrl.startsWith('http') && !imageUrl.startsWith(window.location.origin)) {
      img.crossOrigin = 'anonymous';
    }
    img.src = imageUrl;
    img.onload = () => {
      ctx.globalAlpha = 0.8;
      ctx.drawImage(img, 0, 0, width, height);
      ctx.globalAlpha = 1.0;
    };
    img.onerror = () => {
      ctx.fillStyle = '#333';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#fff';
      ctx.font = '20px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Failed to load', width / 2, height / 2);
    };
  };

  const handleAddToCart = () => {
    if (!selectedDesign) {
      showError?.('Please select a design first');
      return;
    }

    try {
      const canvas = canvasRef.current;
      const dataUrl = canvas?.toDataURL('image/png');

      // Add to cart
      addToCart({
        design: selectedDesign,
        previewUrl: dataUrl,
        product: productSpecs,
        price: productSpecs.price,
        quantity: 1,
      });

      showSuccess?.('Added to cart! 🛒');

      // Call callback if provided
      if (onDesignComplete) {
        onDesignComplete({
          design: selectedDesign,
          previewUrl: dataUrl,
          product: productSpecs,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showError?.('Failed to add to cart. Please try again.');
    }
  };

  return (
    <div style={styles.container}>
      {/* Loading Overlay */}
      <AnimatePresence>
        {isGenerating && (
          <motion.div
            style={styles.loadingOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div style={styles.loadingCard}>
              <div style={styles.loadingSpinner} />
              <h3 style={styles.loadingTitle}>Generating Design</h3>
              <p style={styles.loadingStatus}>{generationStatus}</p>
              <p style={styles.loadingHint}>This usually takes 5-15 seconds...</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Design Your NFC Card</h1>
        <p style={styles.subtitle}>
          Create a unique design in three simple steps
        </p>
      </div>

      {/* Step Indicators */}
      <StepIndicator
        steps={['Choose Design', 'Customize', 'Review & Order']}
        currentStep={step}
      />

      {/* Main Content Grid */}
      <div style={styles.grid}>
        {/* Left: Design Selection */}
        <div style={styles.leftPanel}>
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <h3 style={styles.panelTitle}>Choose Your Design</h3>

                {/* Category Pills */}
                <div style={styles.categoryPills}>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setDesignCategory(cat)}
                      style={{
                        ...styles.pill,
                        ...(designCategory === cat ? styles.pillActive : {})
                      }}
                    >
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Design Grid */}
                <div style={styles.designGrid}>
                  {STOCK_DESIGNS[designCategory]?.map((design) => (
                    <motion.div
                      key={design.id}
                      onClick={() => handleSelectDesign(design)}
                      style={styles.designCard}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {design.category === 'gradient' && (
                        <div style={{ ...styles.designPreview, background: design.preview }} />
                      )}
                      {design.category === 'pattern' && (
                        <div style={{
                          ...styles.designPreview,
                          background: design.background,
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0.6,
                            color: design.foreground,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            fontFamily: 'monospace'
                          }}>
                            {design.patternType}
                          </div>
                        </div>
                      )}
                      {design.thumbnail && (
                        <img src={design.thumbnail} alt={design.name} style={styles.designPreview} />
                      )}
                      <p style={styles.designName}>{design.name}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Upload Option */}
                <div style={styles.divider}>or</div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={styles.uploadButton}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Uploading...' : 'Upload Your Own Design'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />

                {/* AI Generator Teaser */}
                <div style={styles.aiTeaser}>
                  <p style={styles.aiTeaserText}>Want AI-generated designs?</p>
                  <input
                    type="text"
                    placeholder="E.g., 'cyberpunk neon pattern'"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    style={{
                      ...styles.aiInput,
                      transition: 'all 0.2s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = theme.accent;
                      e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = theme.border;
                      e.target.style.boxShadow = 'none';
                    }}
                    disabled={isGenerating}
                  />
                  <motion.button
                    onClick={handleGenerateAI}
                    disabled={isGenerating || !customPrompt}
                    whileHover={!isGenerating && customPrompt ? { scale: 1.02 } : {}}
                    whileTap={!isGenerating && customPrompt ? { scale: 0.98 } : {}}
                    style={{
                      ...styles.aiButton,
                      opacity: isGenerating || !customPrompt ? 0.6 : 1,
                      cursor: isGenerating || !customPrompt ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                    }}
                  >
                    {isGenerating && (
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                      }} />
                    )}
                    {isGenerating ? 'Generating...' : 'Generate with AI'}
                  </motion.button>
                </div>
              </motion.div>
            )}

            {step >= 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h3 style={styles.panelTitle}>Your Selection</h3>

                <div style={styles.selectedCard}>
                  <div style={styles.selectedPreview}>
                    {selectedDesign?.category === 'gradient' && (
                      <div style={{ width: '100%', height: '100%', background: selectedDesign.preview }} />
                    )}
                    {selectedDesign?.category === 'pattern' && (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: selectedDesign.background,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: selectedDesign.foreground,
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        opacity: 0.8
                      }}>
                        {selectedDesign.patternType}
                      </div>
                    )}
                    {selectedDesign?.imageUrl && (
                      <img
                        src={selectedDesign.imageUrl}
                        alt={selectedDesign.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </div>
                  <div style={styles.selectedInfo}>
                    <h4 style={styles.selectedName}>{selectedDesign?.name}</h4>
                    <p style={styles.selectedCategory}>
                      {selectedDesign?.category}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setStep(1)}
                  style={styles.changeButton}
                >
                  ← Change Design
                </button>

                {/* Style Variations */}
                <div style={styles.styleSection}>
                  <h4 style={styles.styleTitle}>
                    Apply AI Style
                  </h4>
                  <p style={styles.styleDescription}>
                    Transform your design with AI-powered styles
                  </p>
                  <div style={styles.styleGrid}>
                    {STYLE_VARIATIONS.map((style) => (
                      <motion.button
                        key={style.id}
                        onClick={() => handleApplyStyle(style)}
                        style={styles.styleCard}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={isGenerating}
                      >
                        <div style={styles.styleIcon}>{style.icon}</div>
                        <div style={styles.styleName}>{style.name}</div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Product Specs */}
                <div style={styles.specsSection}>
                  <h4 style={styles.specsTitle}>Card Specifications</h4>
                  <div style={styles.specsList}>
                    <div style={styles.specItem}>
                      <span style={styles.specLabel}>Material</span>
                      <span style={styles.specValue}>{productSpecs.material}</span>
                    </div>
                    <div style={styles.specItem}>
                      <span style={styles.specLabel}>Size</span>
                      <span style={styles.specValue}>{productSpecs.dimensions}</span>
                    </div>
                    <div style={styles.specItem}>
                      <span style={styles.specLabel}>Technology</span>
                      <span style={styles.specValue}>{productSpecs.technology}</span>
                    </div>
                    <div style={styles.specItem}>
                      <span style={styles.specLabel}>Read Range</span>
                      <span style={styles.specValue}>{productSpecs.readRange}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right: Preview & CTA */}
        <div style={styles.rightPanel}>
          <h3 style={styles.previewTitle}>Preview</h3>

          <div style={styles.canvasContainer}>
            <canvas
              ref={canvasRef}
              width={600}
              height={375}
              style={styles.canvas}
            />
          </div>

          {/* Price & CTA */}
          <div style={styles.ctaSection}>
            <div style={styles.priceRow}>
              <span style={styles.priceLabel}>Price</span>
              <span style={styles.price}>
                ${(productSpecs.price / 100).toFixed(2)}
              </span>
            </div>

            <motion.button
              onClick={handleAddToCart}
              disabled={!selectedDesign}
              whileHover={selectedDesign ? { scale: 1.02 } : {}}
              whileTap={selectedDesign ? { scale: 0.98 } : {}}
              style={{
                ...styles.addToCartButton,
                ...(selectedDesign ? {} : styles.addToCartButtonDisabled),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
              }}
            >
              {selectedDesign ? 'Add to Cart' : 'Select a design first'}
            </motion.button>

            {selectedDesign && (
              <p style={styles.ctaHint}>
                Free shipping on orders over $50
              </p>
            )}
          </div>

          {/* Features */}
          <div style={styles.features}>
            <div style={styles.feature}>
              <span style={styles.featureIcon}>✓</span>
              <span style={styles.featureText}>Waterproof & Durable</span>
            </div>
            <div style={styles.feature}>
              <span style={styles.featureIcon}>✓</span>
              <span style={styles.featureText}>Works with all NFC devices</span>
            </div>
            <div style={styles.feature}>
              <span style={styles.featureIcon}>✓</span>
              <span style={styles.featureText}>Reprogrammable unlimited times</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== Styles ==========
const styles = {
  container: {
    padding: '60px 40px',
    maxWidth: '1400px',
    margin: '0 auto',
    background: theme.bg,
    minHeight: '100vh'
  },
  header: {
    textAlign: 'center',
    marginBottom: '60px'
  },
  title: {
    fontSize: '48px',
    fontWeight: '800',
    margin: '0 0 16px 0',
    background: theme.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'Syne, sans-serif'
  },
  subtitle: {
    fontSize: '18px',
    color: theme.textMuted,
    margin: 0,
    fontFamily: 'DM Sans, sans-serif'
  },
  steps: {
    display: 'flex',
    justifyContent: 'center',
    gap: '60px',
    marginBottom: '60px'
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    opacity: 0.5,
    transition: 'opacity 0.3s'
  },
  stepItemActive: {
    opacity: 1
  },
  stepNumber: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: `2px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '600',
    color: theme.textMuted
  },
  stepNumberActive: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: theme.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '600',
    color: theme.bg,
    border: 'none'
  },
  stepLabel: {
    fontSize: '14px',
    color: theme.text,
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: '500'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px'
  },
  leftPanel: {
    minHeight: '400px'
  },
  rightPanel: {
    background: theme.bgAlt,
    border: `1px solid ${theme.border}`,
    borderRadius: '16px',
    padding: '40px',
    position: 'sticky',
    top: '40px',
    height: 'fit-content'
  },
  panelTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: theme.text,
    margin: '0 0 24px 0',
    fontFamily: 'Syne, sans-serif'
  },
  categoryPills: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    flexWrap: 'wrap'
  },
  pill: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: `1px solid ${theme.border}`,
    background: theme.surface,
    color: theme.textMuted,
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'DM Sans, sans-serif',
    transition: 'all 0.2s'
  },
  pillActive: {
    background: theme.accent,
    color: theme.bg,
    borderColor: theme.accent
  },
  designGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '32px'
  },
  designCard: {
    cursor: 'pointer',
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    padding: '12px',
    background: theme.surface,
    transition: 'all 0.2s'
  },
  designPreview: {
    width: '100%',
    height: '100px',
    borderRadius: '8px',
    marginBottom: '8px',
    objectFit: 'cover'
  },
  designName: {
    fontSize: '12px',
    color: theme.text,
    margin: 0,
    textAlign: 'center',
    fontFamily: 'DM Sans, sans-serif'
  },
  divider: {
    textAlign: 'center',
    color: theme.textMuted,
    fontSize: '14px',
    margin: '24px 0',
    fontFamily: 'DM Sans, sans-serif'
  },
  uploadButton: {
    width: '100%',
    padding: '16px',
    background: theme.surface,
    border: `2px dashed ${theme.border}`,
    borderRadius: '12px',
    color: theme.text,
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'DM Sans, sans-serif'
  },
  aiTeaser: {
    marginTop: '32px',
    padding: '24px',
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px'
  },
  aiTeaserText: {
    fontSize: '14px',
    color: theme.textMuted,
    margin: '0 0 12px 0',
    fontFamily: 'DM Sans, sans-serif'
  },
  aiInput: {
    width: '100%',
    padding: '12px',
    background: theme.bgAlt,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    color: theme.text,
    fontSize: '14px',
    fontFamily: 'DM Sans, sans-serif',
    marginBottom: '12px'
  },
  aiButton: {
    width: '100%',
    padding: '12px',
    background: theme.gradient,
    border: 'none',
    borderRadius: '8px',
    color: theme.bg,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif'
  },
  selectedCard: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px'
  },
  selectedPreview: {
    width: '100%',
    height: '150px',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '12px'
  },
  selectedInfo: {
    textAlign: 'center'
  },
  selectedName: {
    fontSize: '18px',
    fontWeight: '600',
    color: theme.text,
    margin: '0 0 4px 0',
    fontFamily: 'Syne, sans-serif'
  },
  selectedCategory: {
    fontSize: '12px',
    color: theme.textMuted,
    margin: 0,
    fontFamily: 'DM Sans, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  changeButton: {
    width: '100%',
    padding: '12px',
    background: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    color: theme.text,
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    marginBottom: '32px'
  },
  specsSection: {
    marginTop: '32px'
  },
  specsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: theme.text,
    margin: '0 0 16px 0',
    fontFamily: 'Syne, sans-serif'
  },
  specsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  specItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
    background: theme.surface,
    borderRadius: '8px'
  },
  specLabel: {
    fontSize: '14px',
    color: theme.textMuted,
    fontFamily: 'DM Sans, sans-serif'
  },
  specValue: {
    fontSize: '14px',
    color: theme.text,
    fontWeight: '500',
    fontFamily: 'DM Sans, sans-serif'
  },
  previewTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: theme.text,
    margin: '0 0 24px 0',
    fontFamily: 'Syne, sans-serif'
  },
  canvasContainer: {
    marginBottom: '32px'
  },
  canvas: {
    width: '100%',
    height: 'auto',
    borderRadius: '12px',
    background: 'transparent'
  },
  ctaSection: {
    marginBottom: '32px'
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  priceLabel: {
    fontSize: '16px',
    color: theme.textMuted,
    fontFamily: 'DM Sans, sans-serif'
  },
  price: {
    fontSize: '32px',
    fontWeight: '700',
    color: theme.text,
    fontFamily: 'Syne, sans-serif'
  },
  addToCartButton: {
    width: '100%',
    padding: '18px',
    background: theme.gradient,
    border: 'none',
    borderRadius: '12px',
    color: theme.bg,
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'Syne, sans-serif',
    transition: 'transform 0.2s'
  },
  addToCartButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  ctaHint: {
    fontSize: '12px',
    color: theme.textMuted,
    textAlign: 'center',
    margin: '12px 0 0 0',
    fontFamily: 'DM Sans, sans-serif'
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  featureIcon: {
    fontSize: '16px',
    color: theme.accent
  },
  featureText: {
    fontSize: '14px',
    color: theme.text,
    fontFamily: 'DM Sans, sans-serif'
  },
  // Loading Overlay
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(10, 10, 11, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(8px)'
  },
  loadingCard: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '16px',
    padding: '48px',
    textAlign: 'center',
    maxWidth: '400px'
  },
  loadingSpinner: {
    width: '64px',
    height: '64px',
    margin: '0 auto 24px',
    border: `4px solid ${theme.border}`,
    borderTop: `4px solid ${theme.accent}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: theme.text,
    margin: '0 0 12px 0',
    fontFamily: 'Syne, sans-serif'
  },
  loadingStatus: {
    fontSize: '16px',
    color: theme.accent,
    margin: '0 0 8px 0',
    fontFamily: 'DM Sans, sans-serif'
  },
  loadingHint: {
    fontSize: '14px',
    color: theme.textMuted,
    margin: 0,
    fontFamily: 'DM Sans, sans-serif'
  },
  // Style Variations
  styleSection: {
    marginTop: '32px',
    marginBottom: '32px',
    padding: '24px',
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px'
  },
  styleTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: theme.text,
    margin: '0 0 8px 0',
    fontFamily: 'Syne, sans-serif'
  },
  styleDescription: {
    fontSize: '14px',
    color: theme.textMuted,
    margin: '0 0 20px 0',
    fontFamily: 'DM Sans, sans-serif'
  },
  styleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px'
  },
  styleCard: {
    padding: '16px 8px',
    background: theme.bgAlt,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center'
  },
  styleIcon: {
    fontSize: '24px',
    marginBottom: '8px'
  },
  styleName: {
    fontSize: '12px',
    color: theme.text,
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: '500'
  }
};
